// Step 09/09 — Finish
// Shows a completed checklist, any skipped/pending items, a recap email send,
// and a final Notion sync. Agent Services will follow up on anything unchecked.

import React from "react";
import { IcArrow, IcCheck, IcMail } from "./shared.jsx";
import { StepHeader, FooterNav } from "./steps_early.jsx";
import { api } from "../lib/api.js";
import { agreementSignedState } from "../lib/agreement-signed.js";

const { useState: fS, useEffect: fE, useRef: fR } = React;

function StepFinish({ agent, agreements, gate, serverDocs, associations, listings, escrows, bbas, adreConfirmed, notionId, updateTracker, onNext , eyebrow}) {
  const [emailStatus, setEmailStatus] = fS("idle"); // idle | sending | sent | error
  const [notionSynced, setNotionSynced] = fS(false);

  // Signed-ness is decided in one shared, tested place — see lib/agreement-signed.js
  // for why (this rule has been got wrong twice, both times reporting an executed
  // contract as unsigned, and these strings are written into the recap email, the
  // Notion status and onboarding_milestones.notes).
  const recruit = !!agent?.recruit;
  const { signed: packageSigned, when: packageWhen, label: agreementLabel } =
    agreementSignedState({ recruit, agreements, gate, serverDocs });
  const isFoundingAgent  = !recruit && !!agreements.package?.foundingAgent;

  // Derive completion state from each data source
  const assocSel         = associations?.assoc || [];
  const formStatus       = associations?.formStatus || {};
  const assocDone        = assocSel.length > 0 &&
    assocSel.every(k => { const s = formStatus[`assoc:${k}`]; return s === "confirmed" || s === "signed"; });
  const assocSkipped     = assocSel.length === 0;

  const listingsDone     = listings.length === 0 || listings.every(l => l.transferStatus === "transferred");
  const listingsCount    = listings.filter(l => l.transferStatus === "transferred").length;

  const escrowList       = escrows || [];
  const escrowsDone      = escrowList.length === 0 || escrowList.every(e => e.transferStatus === "transferred");
  const escrowsCount     = escrowList.filter(e => e.transferStatus === "transferred").length;

  const bbaList          = (bbas || []).filter(b => b.selected);
  const bbasDone         = bbaList.length === 0 || bbaList.every(b => b.transferStatus === "transferred");
  const bbasCount        = bbaList.filter(b => b.transferStatus === "transferred").length;

  // Transfer steps removed from the wizard (gate.skipTransfers, backed by
  // ONBOARDING_TRANSFER_STEPS). When they are gone the listings/escrow/BBA
  // fetches never run, so those arrays are empty for the trivial reason that
  // nobody looked.
  //
  // Every line below keys off `.length === 0`, so leaving them in would put
  // "Listings — no active listings on file" in the COMPLETED column — telling the
  // agent, the recap email and the staff summary that they have none, on the
  // strength of a lookup we skipped. Silence is the honest answer; the same lines
  // return unchanged the moment the steps do.
  const showTransfers = !gate?.skipTransfers;

  // Build completed + skipped item lists
  const completed = [
    // Server-verified, unlike most of this list: gate.welcomeDone is
    // `intake.welcome_form_id IS NOT NULL`, so the row demonstrably exists. It
    // leads because it is the first step the agent completes, and because its
    // absence is what silently strands an onboarding at the gate.
    gate?.welcomeDone && "Welcome to the Team form submitted",
    packageSigned && `${agreementLabel} signed${packageWhen ? ` (${packageWhen})` : ""}`,
    packageSigned && isFoundingAgent && "Founding Agent Addendum included",
    adreConfirmed && "ADRE license transfer submitted",
    assocDone     && `REALTOR association membership transfer submitted`,
    showTransfers && (listingsDone && listings.length > 0)  && `${listingsCount} listing${listingsCount  === 1 ? "" : "s"} transferred`,
    showTransfers && listings.length  === 0 && "Listings — no active listings on file",
    showTransfers && (escrowsDone && escrowList.length > 0) && `${escrowsCount} pending escrow${escrowsCount  === 1 ? "" : "s"} transferred`,
    showTransfers && escrowList.length === 0 && "Escrows — none on file",
    showTransfers && (bbasDone && bbaList.length > 0)       && `${bbasCount} buyer agreement${bbasCount  === 1 ? "" : "s"} transferred`,
    showTransfers && bbaList.length === 0 && "Buyer agreements — none on file",
  ].filter(Boolean);

  const skipped = [
    !packageSigned  && `${agreementLabel} — not yet signed`,
    !adreConfirmed  && "ADRE license transfer — not yet confirmed",
    assocSkipped    && "REALTOR association membership transfer — not submitted",
    !assocDone && !assocSkipped && `Association membership transfer — ${assocSel.length - assocSel.filter(k => { const s = formStatus[`assoc:${k}`]; return s === "confirmed" || s === "signed"; }).length} pending`,
    showTransfers && (!listingsDone && listings.length > 0)    &&  `${listings.filter(l => l.transferStatus !== "transferred").length} listing transfer${listings.filter(l => l.transferStatus !== "transferred").length === 1 ? "" : "s"} pending`,
    showTransfers && (!escrowsDone  && escrowList.length > 0)  &&  `${escrowList.filter(e => e.transferStatus !== "transferred").length} escrow transfer${escrowList.filter(e => e.transferStatus !== "transferred").length === 1 ? "" : "s"} pending`,
    showTransfers && (!bbasDone     && bbaList.length > 0)     &&  `${bbaList.filter(b => b.transferStatus !== "transferred").length} buyer agreement transfer${bbaList.filter(b => b.transferStatus !== "transferred").length === 1 ? "" : "s"} pending`,
  ].filter(Boolean);

  const allComplete = skipped.length === 0 && packageSigned;

  // Sync final state to Notion once on mount
  fE(() => {
    if (!notionId || notionSynced) return;
    setNotionSynced(true);
    const notes = skipped.length ? `Skipped: ${skipped.join("; ")}` : undefined;
    updateTracker?.({
      // Same reason the recap rows are suppressed: with the transfer steps gone
      // these flags would all read "done" off empty arrays, writing "listings
      // transferred" into Notion for an agent whose listings were never checked.
      ...(showTransfers ? {
        listingsTransferred: listingsDone,
        escrowsTransferred:  escrowsDone,
        bbasTransferred:     bbasDone,
      } : {}),
      status:              allComplete ? "Complete" : "Needs Follow-up",
      ...(notes ? { notes } : {}),
    }).catch(() => {});
  }, [notionId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function sendRecapEmail({ resend = false } = {}) {
    setEmailStatus(resend ? "resending" : "sending");
    try {
      await api.post("/v1/recap-email", { completed, skipped, resend });
      setEmailStatus("sent");
      updateTracker?.({ recapEmailSent: true }).catch(() => {});
    } catch {
      setEmailStatus("error");
    }
  }

  // AUTO-SEND on reaching Finish. The recap used to require a button press, which
  // put two gates in front of the one email carrying a recruit's mailbox address
  // and password — reach Finish, then click — and in five recruits nobody cleared
  // both. Bethanie Bakarich and Justin Reiss each sat with a live mailbox and an
  // unread Circle invite inside it.
  //
  // Guarded by a ref rather than by emailStatus so a re-render cannot fire it
  // twice, and the server is idempotent regardless (it only forces on an explicit
  // resend). If it already went — the agent came back to this screen — the gate
  // says so and we leave it alone.
  const autoSent = fR(false);
  fE(() => {
    if (autoSent.current) return;
    if (gate?.recapSent) { autoSent.current = true; setEmailStatus("sent"); return; }
    if (!agent?.email) return;
    autoSent.current = true;
    sendRecapEmail();
  }, [gate?.recapSent, agent?.email]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={{maxWidth: 800}}>
      <StepHeader eyebrow={eyebrow || "Step 09 / 09"}
        title={allComplete ? "You're with EveryPlace." : "Onboarding summary."}
        subtitle={allComplete
          ? "All steps are complete. Welcome home."
          : "Your agreements are signed and your onboarding is underway. Agent Services will follow up on any open items."}
      />

      {/* Completed items */}
      <div className="ep-card" style={{padding: 28, marginBottom: 24,
        borderColor: "var(--ep-avocado)", background: "rgba(204,207,129,0.1)"}}>
        <div className="ep-eyebrow" style={{color: "var(--ep-down-to-earth)", marginBottom: 16}}>Completed</div>
        <div className="vstack" style={{gap: 10}}>
          {completed.map((item, i) => (
            <div key={i} style={{display: "flex", alignItems: "flex-start", gap: 12}}>
              <div style={{width: 22, height: 22, borderRadius: 999, background: "var(--ep-avocado)",
                display: "flex", alignItems: "center", justifyContent: "center",
                color: "var(--ep-origin)", flexShrink: 0, marginTop: 1}}>
                <IcCheck size={12}/>
              </div>
              <span style={{fontSize: 14, color: "var(--ep-origin)", lineHeight: 1.5}}>{item}</span>
            </div>
          ))}
          {completed.length === 0 && (
            <div style={{fontSize: 14, color: "var(--ep-fg-muted)"}}>No items completed.</div>
          )}
        </div>
      </div>

      {/* Skipped / pending items */}
      {skipped.length > 0 && (
        <div className="ep-card" style={{padding: 28, marginBottom: 24,
          borderColor: "var(--ep-high-noon)", background: "rgba(255,200,80,0.07)"}}>
          <div className="ep-eyebrow" style={{color: "var(--ep-sunset)", marginBottom: 4}}>Items to complete</div>
          <p style={{fontSize: 13, color: "var(--ep-fg-muted)", marginBottom: 16, maxWidth: "56ch"}}>
            Agent Services has been notified and will follow up with you on the items below.
          </p>
          <div className="vstack" style={{gap: 10}}>
            {skipped.map((item, i) => (
              <div key={i} style={{display: "flex", alignItems: "flex-start", gap: 12}}>
                <div style={{width: 22, height: 22, borderRadius: 999,
                  border: "1.5px dashed var(--ep-high-noon)",
                  flexShrink: 0, marginTop: 1}}/>
                <span style={{fontSize: 14, color: "var(--ep-fg)", lineHeight: 1.5}}>{item}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recap email */}
      <div style={{padding: "22px 28px", background: "var(--ep-origin)", color: "var(--ep-saguaro-blossom)",
        borderRadius: 2, display: "flex", alignItems: "center", justifyContent: "space-between",
        gap: 20, flexWrap: "wrap", marginBottom: emailStatus === "error" ? 8 : 32}}>
        <div>
          <div className="ep-eyebrow" style={{color: "rgba(255,245,228,0.75)"}}>Recap Email</div>
          <div style={{font: "300 20px/1.2 var(--ep-font-display)", margin: "6px 0 4px"}}>
            {emailStatus === "sent" ? `Sent to ${agent.email}` : `Sending to ${agent.email}…`}
          </div>
          <div style={{fontSize: 13, color: "rgba(255,245,228,0.75)"}}>
            {emailStatus === "sent"
              ? "A full summary of your onboarding, including your team email details."
              : "A summary of completed and pending items for your records."}
          </div>
        </div>
        {emailStatus === "sent" ? (
          <div style={{display: "flex", alignItems: "center", gap: 12}}>
            <span className="ep-badge signed"><IcCheck size={10}/> Sent</span>
            {/* Resend, not send: the recap is no longer the agent's job to
                trigger. Kept so someone who deletes it is not stuck asking. */}
            <button className="ep-btn ghost sm"
              style={{color: "var(--ep-saguaro-blossom)", borderColor: "rgba(255,245,228,0.4)"}}
              onClick={() => sendRecapEmail({ resend: true })}
              disabled={emailStatus === "resending"}>
              {emailStatus === "resending" ? "Sending…" : "Resend"}
            </button>
          </div>
        ) : (
          <span className="ep-badge"><IcMail size={10}/>&nbsp;Sending…</span>
        )}
      </div>
      {emailStatus === "error" && (
        <div style={{fontSize: 12, color: "var(--ep-rosewood)", marginBottom: 32}}>
          Failed to send — please try again or contact onboard@everyplace.com.
        </div>
      )}

      {/* Completion banner */}
      {allComplete && (
        <div style={{padding: "40px 32px", textAlign: "center", background: "var(--ep-avocado)", borderRadius: 2}}>
          <IcCheck size={40}/>
          <h2 style={{font: "300 48px/1.1 var(--ep-font-display)", margin: "16px 0 10px", color: "var(--ep-origin)"}}>
            Welcome to EveryPlace.
          </h2>
          <p style={{fontSize: 16, color: "var(--ep-origin)", opacity: 0.9, maxWidth: "44ch", margin: "0 auto 24px", lineHeight: 1.55}}>
            Your agreements are signed and your license transfer is in motion. Amber Valentine will countersign within one business day.
          </p>
          <button className="ep-btn primary" onClick={onNext}>
            Go to Circle Training <IcArrow/>
          </button>
        </div>
      )}

      {/* Finish is no longer terminal — Circle training follows. The banner above
          only renders when everything is complete, so agents with open items
          still need a way forward. */}
      {!allComplete && (
        <FooterNav onNext={onNext} nextLabel="Continue to Circle Training"/>
      )}
    </div>
  );
}

export { StepFinish };
